import { useCallback, useEffect, useRef } from 'react';
import { api } from '../services/api';
import { IS_FACTURACION } from '../config';
import { playNewOrderSound, unlockNewOrderSound } from '../utils/newOrderSound';

/** Detecta pedidos confirmados nuevos; suena sin trabar la UI. */
export function useNewOrderAlert(pin, { enabled = true, refreshKey = 0, onNewOrder } = {}) {
  const knownIdsRef = useRef(null);
  const onNewRef = useRef(onNewOrder);
  onNewRef.current = onNewOrder;
  const inFlightRef = useRef(false);
  const remindersRef = useRef(0);

  const check = useCallback(async () => {
    if (!enabled || !pin || inFlightRef.current) return;
    inFlightRef.current = true;
    try {
      const res = await api.getPedidos({
        estado: IS_FACTURACION ? 'pendiente' : 'confirmado',
        limit: 100,
      });
      const list = res.data || [];
      const ids = new Set(list.map((p) => p.id));

      if (knownIdsRef.current !== null) {
        const nuevos = [];
        for (const id of ids) {
          if (!knownIdsRef.current.has(id)) {
            const pedido = list.find((p) => p.id === id);
            if (pedido) nuevos.push(pedido);
          }
        }
        if (nuevos.length) {
          remindersRef.current = 0;
          // Reintentar unlock por si el tablet suspendió el audio
          await unlockNewOrderSound();
          await playNewOrderSound({
            title: nuevos.length > 1 ? `${nuevos.length} pedidos` : nuevos[0].numero || 'Pedido',
          });
          onNewRef.current?.(nuevos[0], nuevos.length);
        } else if (
          list.length > 0 &&
          document.visibilityState === 'hidden' &&
          remindersRef.current < 2
        ) {
          remindersRef.current += 1;
          await unlockNewOrderSound();
          await playNewOrderSound({ title: `${list.length} ${IS_FACTURACION ? 'por facturar' : 'por preparar'}`, short: true });
        }
        if (!list.length) remindersRef.current = 0;
      }
      knownIdsRef.current = ids;
    } catch {
      /* ignore */
    } finally {
      inFlightRef.current = false;
    }
  }, [enabled, pin]);

  useEffect(() => {
    check();
    const t = setInterval(check, 60000);
    const onVis = () => {
      if (document.visibilityState === 'visible') check();
    };
    const onOnline = () => check();
    const onInv = (ev) => {
      const domains = ev.detail?.domains || [];
      if (domains.includes('order')) check();
    };
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('online', onOnline);
    window.addEventListener('oh:invalidate', onInv);
    return () => {
      clearInterval(t);
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('online', onOnline);
      window.removeEventListener('oh:invalidate', onInv);
    };
  }, [check, refreshKey]);
}
